import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import {
  Download,
  RefreshCw,
  Upload,
  Users,
  ChevronDown,
  CheckCircle,
  CheckCircle2,
  List,
  Info,
  HelpCircle,
  ClipboardList,
  AlertCircle,
  Loader2,
  Lock,
  X,
  Check,
} from "lucide-react";
import {
  bulkUpdateAttendanceStatus,
  fetchAttendance,
  fetchMyOrganisationSummary,
  normalizeAttendanceStatus,
  toApiAttendanceStatus,
  upsertAttendanceRecord,
  updateAttendance,
} from "./attendanceApi";
import { fetchWfEnabledModes } from "./wfApi";
import { getEmployees } from "../Employees/EmployeeApi";
import { ATTENDANCE_LEGACY_PATHS } from "./attendanceNavConfig";
import { fetchLeaveTypes } from "../leave/leaveApi";
import { ALL_LEAVE_TYPES } from "../../components/pages/Leave/leavePackageCatalog";
import "../../styles/attendance-mvp.css";

const PAGE_SIZE = 10;

const STATUS_OPTIONS = [
  { value: "", label: "All Status" },
  { value: "P", label: "Present" },
  { value: "A", label: "Absent (LOP)" },
  { value: "L", label: "Leave" },
  { value: "HD", label: "Half Day" },
  { value: "WO", label: "Week Off" },
  { value: "H", label: "Holiday" },
];

const EDITABLE_STATUSES = [
  { value: "P", label: "Present" },
  { value: "A", label: "Absent (LOP)" },
  { value: "HD", label: "Half Day" },
  { value: "L", label: "Leave" },
];

const DEFAULT_LEAVE_TYPES = ALL_LEAVE_TYPES
  .filter((t) => !t.admin_only && t.code !== "LOP" && t.code !== "HD" && t.code !== "CO" && t.code !== "WFH")
  .map((t) => ({ code: t.code, name: t.name }));

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function formatTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
}

const AVATAR_TONES = [
  "bg-blue-600", "bg-emerald-600", "bg-amber-600", "bg-rose-600",
  "bg-violet-600", "bg-cyan-600", "bg-pink-600", "bg-teal-600",
];

function empInitials(name = "") {
  return name.split(" ").filter(Boolean).slice(0, 2).map((p) => p[0]).join("").toUpperCase() || "?";
}

function empAvatarColor(name = "") {
  let n = 0;
  for (let i = 0; i < name.length; i++) n = (n + name.charCodeAt(i)) % AVATAR_TONES.length;
  return AVATAR_TONES[n];
}

function sourceBadge(src) {
  const s = String(src || "").toLowerCase();
  if (s.includes("leave")) return { label: "Leave Request", cls: "bg-amber-100 text-amber-800 border-amber-200" };
  if (s.includes("brixigo_cloud") || s.includes("biometric") || s.includes("punch")) {
    return { label: "Biometric Cloud", cls: "bg-cyan-100 text-cyan-800 border-cyan-200" };
  }
  if (s.includes("excel") || s.includes("upload")) return { label: "Excel Upload", cls: "bg-green-100 text-green-800 border-green-200" };
  if (s.includes("manual")) return { label: "Manual Entry", cls: "bg-blue-100 text-blue-800 border-blue-200" };
  if (s.includes("default_present")) return { label: "Default Present", cls: "bg-slate-100 text-slate-600 border-slate-200" };
  return { label: "System Default", cls: "bg-slate-100 text-slate-700 border-slate-200" };
}

function statusBadge(code) {
  const c = normalizeAttendanceStatus(code);
  if (c === "P") return { label: "Present", code: "P", cls: "att-mvp-badge--present" };
  if (c === "A") return { label: "Absent (LOP)", code: "A", cls: "att-mvp-badge--absent" };
  if (["L", "PL", "UL"].includes(c)) return { label: "Leave", code: c, cls: "att-mvp-badge--leave" };
  if (c === "HD") return { label: "Half Day", code: "HD", cls: "att-mvp-badge--leave" };
  if (c === "WO") return { label: "Week Off", code: "WO", cls: "" };
  if (c === "H") return { label: "Holiday", code: "H", cls: "" };
  return { label: c || "—", code: c || "", cls: "" };
}

function isOrgLevelStatus(code) {
  return normalizeAttendanceStatus(code) === "H";
}

export default function AttendanceTablePage() {
  const [organisationId, setOrganisationId] = useState("");
  const [attendance, setAttendance] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [, setEnabledModes] = useState([]);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  const [filterDate, setFilterDate] = useState(todayIso());
  const [filterDept, setFilterDept] = useState("");
  const [filterEmployee, setFilterEmployee] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkStatus, setBulkStatus] = useState("P");
  const [bulkLeaveType, setBulkLeaveType] = useState("");
  const [bulkValidation, setBulkValidation] = useState("");
  const [rowEdit, setRowEdit] = useState(null);
  const [rowEditStatus, setRowEditStatus] = useState("P");
  const [rowEditLeaveType, setRowEditLeaveType] = useState("");
  const [rowEditBusy, setRowEditBusy] = useState(false);
  const [rowEditSuccess, setRowEditSuccess] = useState(false);
  const [rowEditValidation, setRowEditValidation] = useState("");
  const [infoModal, setInfoModal] = useState(null);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [statusTab, setStatusTab] = useState("");
  const [leaveTypes, setLeaveTypes] = useState(DEFAULT_LEAVE_TYPES);
  const actionsRef = useRef(null);

  const employeeMaps = useMemo(() => {
    const byId = new Map();
    const depts = new Map();
    for (const e of employees) {
      byId.set(String(e.employee_id), e);
      if (e.department?.department_id) {
        depts.set(String(e.department.department_id), e.department.name || "Department");
      }
    }
    return { byId, depts: [...depts.entries()].map(([id, name]) => ({ id, name })) };
  }, [employees]);

  const loadEmployees = useCallback(async () => {
    try {
      const rows = await getEmployees({ page: 1, page_size: 500, active_only: true });
      const items = Array.isArray(rows?.items) ? rows.items : Array.isArray(rows) ? rows : [];
      setEmployees(items);
    } catch {
      setEmployees([]);
    }
  }, []);

  const loadAttendance = useCallback(async (orgId, date, deptId) => {
    if (!orgId || !date) return;
    setLoading(true);
    setError("");
    try {
      const params = {
        organisation_id: orgId,
        from_date: date,
        to_date: date,
      };
      if (deptId) params.department_id = deptId;
      const data = await fetchAttendance(params);
      setAttendance(Array.isArray(data) ? data : []);
    } catch (e) {
      setAttendance([]);
      setError(e?.message || "Failed to load attendance");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLeaveTypes = async (orgId) => {
    try {
      const types = await fetchLeaveTypes(orgId);
      if (Array.isArray(types) && types.length) {
        setLeaveTypes(
          types
            .filter((t) => (t.code || t.leave_code) && (t.code || t.leave_code) !== "HD" && (t.code || t.leave_code) !== "WFH")
            .map((t) => ({ code: t.code || t.leave_code, name: t.name || t.leave_name || t.code || t.leave_code }))
        );
      }
    } catch {
      /* keep DEFAULT_LEAVE_TYPES */
    }
  };

  useEffect(() => {
    (async () => {
      try {
        await loadEmployees();
        const [org, modesRes] = await Promise.all([
          fetchMyOrganisationSummary(),
          fetchWfEnabledModes().catch(() => []),
        ]);
        setOrganisationId(org?.id || "");
        if (org?.id) loadLeaveTypes(org.id);
        const modes = Array.isArray(modesRes) ? modesRes : modesRes?.enabled_modes || modesRes?.modes || [];
        setEnabledModes(modes);
      } catch (e) {
        setError(e?.message || "Failed to load organisation context");
      }
    })();
  }, [loadEmployees]);

  useEffect(() => {
    if (organisationId) loadAttendance(organisationId, filterDate, filterDept);
  }, [organisationId, filterDate, filterDept, loadAttendance]);



  const rows = useMemo(() => {
    return attendance
      .map((r) => {
        const emp = employeeMaps.byId.get(String(r.employee_id));
        const name =
          [emp?.first_name, emp?.middle_name, emp?.last_name].filter(Boolean).join(" ").trim() ||
          r.employee_name ||
          "—";
        const dept = emp?.department?.name || "—";
        const deptId = emp?.department?.department_id;
        const st = statusBadge(r.status);
        const leaveTypeCode = parseLeaveTypeFromRemarks(r.remarks);
        return {
          id: r.attendance_id || `${r.employee_id}-${r.work_date}`,
          attendanceId: r.attendance_id || null,
          name,
          empId: r.employee_id,
          date: r.work_date,
          inTime: formatTime(r.time_in || r.check_in_time),
          outTime: formatTime(r.time_out || r.check_out_time),
          status: st,
          rawStatus: r.status,
          source: r.source || r.attendance_source || "Manual Entry",
          dept,
          deptId,
          updatedAt: r.updated_at || r.created_at,
          updatedBy: r.updated_by_name || null,
          remarks: r.remarks || "",
          leaveTypeCode,
        };
      })
      .filter((row) => {
        if (filterDept && String(row.deptId) !== filterDept) return false;
        if (filterEmployee && String(row.empId) !== filterEmployee) return false;
        if (filterStatus) {
          const code = normalizeAttendanceStatus(filterStatus);
          const rowCode = normalizeAttendanceStatus(row.rawStatus || row.status.code);
          if (code !== rowCode) return false;
        }
        return true;
      });
  }, [attendance, employeeMaps.byId, filterDept, filterEmployee, filterStatus]);

  const audit = useMemo(() => {
    const sorted = [...attendance].sort((a, b) => {
      const ta = new Date(a.updated_at || a.created_at || 0).getTime();
      const tb = new Date(b.updated_at || b.created_at || 0).getTime();
      return tb - ta;
    });
    const latest = sorted[0];
    if (!latest) return null;
    return {
      modifiedDate: latest.updated_at || latest.created_at,
      modifiedBy: latest.updated_by_name || "—",
      source: latest.source || latest.attendance_source || "Manual Entry",
    };
  }, [attendance]);

  const statusCounts = useMemo(() => {
    const counts = { all: rows.length, P: 0, L: 0, A: 0 };
    for (const r of rows) {
      const c = normalizeAttendanceStatus(r.rawStatus || r.status.code);
      if (c === "P") counts.P++;
      else if (["L", "PL", "UL"].includes(c)) counts.L++;
      else if (c === "A") counts.A++;
    }
    return counts;
  }, [rows]);

  const displayRows = useMemo(() => {
    if (!statusTab) return rows;
    return rows.filter((r) => {
      const c = normalizeAttendanceStatus(r.rawStatus || r.status.code);
      if (statusTab === "P") return c === "P";
      if (statusTab === "L") return ["L", "PL", "UL"].includes(c);
      if (statusTab === "A") return c === "A";
      return true;
    });
  }, [rows, statusTab]);

  useEffect(() => {
    const handler = (e) => {
      if (actionsRef.current && !actionsRef.current.contains(e.target)) setActionsOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const totalPages = Math.max(1, Math.ceil(displayRows.length / PAGE_SIZE));
  const pagedRows = displayRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const firstIdx = displayRows.length ? (page - 1) * PAGE_SIZE + 1 : 0;
  const lastIdx = Math.min(page * PAGE_SIZE, displayRows.length);

  const resetFilters = () => {
    setFilterDate(todayIso());
    setFilterDept("");
    setFilterEmployee("");
    setFilterStatus("");
    setPage(1);
  };

  const applyFilters = () => {
    setPage(1);
    if (organisationId) loadAttendance(organisationId, filterDate, filterDept);
  };

  const toggleRow = (empId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const key = String(empId);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const openBulkModal = () => {
    if (!selectedIds.size) {
      setError("Select one or more employees with the checkboxes before Bulk Update Status.");
      setInfo("");
      return;
    }
    setError("");
    setBulkStatus("P");
    setBulkLeaveType("");
    setBulkValidation("");
    setBulkOpen(true);
  };

  const openRowStatusEdit = (row) => {
    const code = normalizeAttendanceStatus(row.rawStatus || row.status?.code);
    if (code === "H") {
      setInfoModal({ type: "H", holidayName: "" });
      return;
    }
    setRowEdit(row);
    setRowEditStatus(["P", "A", "HD", "L"].includes(code) ? code : "P");
    setRowEditLeaveType(row.leaveTypeCode || "");
    setRowEditValidation("");
    setRowEditSuccess(false);
    setError("");
    setInfo("");
  };

  const applyBulkStatus = async () => {
    if (!organisationId || !filterDate) return;
    if (!selectedIds.size) {
      setError("Select at least one employee to update.");
      return;
    }
    if (bulkStatus === "L" && !bulkLeaveType) {
      setBulkValidation("Please select a leave type.");
      return;
    }
    const ids = [...selectedIds];
    setBulkBusy(true);
    setError("");
    setInfo("");
    setBulkValidation("");
    try {
      const remarks = bulkStatus === "L" && bulkLeaveType ? `leave:${bulkLeaveType}` : undefined;
      const result = await bulkUpdateAttendanceStatus({
        organisation_id: organisationId,
        work_date: filterDate,
        status: toApiAttendanceStatus(bulkStatus),
        employee_ids: ids,
        department_id: filterDept || undefined,
        remarks,
      });
      const saved = (result?.created || 0) + (result?.updated || 0);
      setInfo(`Updated status for ${saved || ids.length} employee(s).`);
      setBulkOpen(false);
      setSelectedIds(new Set());
      await loadAttendance(organisationId, filterDate, filterDept);
    } catch (e) {
      setError(e.message || "Bulk status update failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const applyRowStatus = async () => {
    if (!organisationId || !rowEdit) return;
    if (rowEditStatus === "L" && !rowEditLeaveType) {
      setRowEditValidation("Please select a leave type.");
      return;
    }
    setRowEditBusy(true);
    setRowEditValidation("");
    setError("");
    setInfo("");
    try {
      const remarks = rowEditStatus === "L" && rowEditLeaveType ? `leave:${rowEditLeaveType}` : "records-ui";
      const workHours = rowEditStatus === "A" ? 0 : rowEditStatus === "HD" ? 4 : 8;
      if (rowEdit.attendanceId) {
        await updateAttendance(rowEdit.attendanceId, { status: rowEditStatus, work_hours: workHours, remarks });
      } else {
        await upsertAttendanceRecord({
          organisation_id: organisationId,
          employee_id: rowEdit.empId,
          work_date: rowEdit.date || filterDate,
          status: rowEditStatus,
          work_hours: workHours,
          remarks,
        });
      }
      setRowEditBusy(false);
      setRowEditSuccess(true);
      const savedName = rowEdit.name;
      setTimeout(async () => {
        setInfo(`Updated status for ${savedName}.`);
        setRowEdit(null);
        setRowEditSuccess(false);
        await loadAttendance(organisationId, filterDate, filterDept);
      }, 1000);
    } catch (e) {
      setRowEditBusy(false);
      setRowEditValidation(e.message || "Failed to update employee status. Please try again.");
    }
  };

  return (
    <div className="att-mvp att-mvp-records-page" style={{ maxWidth: "100%" }}>
      {/* 1) HERO BANNER — gradient matching Leave module */}
      <div className="rounded-2xl px-6 py-5 mb-5 shadow-md" style={{ background: "linear-gradient(135deg, #0f2d52 0%, #1a437d 55%, #2a5fa3 100%)" }}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center shadow-sm">
              <ClipboardList size={22} className="text-white" />
            </div>
            <div>
              <h1 className="text-[20px] font-extrabold tracking-tight leading-tight" style={{ color: "#ffffff" }}>Attendance Records</h1>
              <p className="text-[13px] mt-0.5" style={{ color: "rgba(255,255,255,0.75)" }}>View, update and manage employee attendance records</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="inline-flex items-center gap-1.5 px-4 py-2 text-[13px] font-semibold text-white bg-white/15 hover:bg-white/25 backdrop-blur rounded-xl border border-white/20 transition"
              onClick={() => loadAttendance(organisationId, filterDate, filterDept)}
              disabled={!organisationId || loading}
            >
              <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
            <div className="relative" ref={actionsRef}>
              <button
                type="button"
                className="inline-flex items-center gap-1.5 px-4 py-2 text-[13px] font-bold text-blue-700 bg-white hover:bg-blue-50 rounded-xl shadow-sm transition"
                onClick={() => setActionsOpen((v) => !v)}
              >
                <Download size={15} />
                Import &amp; Actions
                <ChevronDown size={14} className={`transition-transform ${actionsOpen ? "rotate-180" : ""}`} />
              </button>
              {actionsOpen && (
                <div className="absolute right-0 top-full mt-2 w-60 bg-white rounded-xl border border-slate-200 shadow-[0_8px_32px_rgba(15,23,42,0.12)] z-30 py-1.5 overflow-hidden">
                  <Link to={ATTENDANCE_LEGACY_PATHS.import} className="flex items-center gap-3 px-4 py-2.5 text-[13px] text-slate-700 hover:bg-slate-50 transition" onClick={() => setActionsOpen(false)}>
                    <Download size={16} className="text-blue-600" />
                    Download Template
                  </Link>
                  <Link to={ATTENDANCE_LEGACY_PATHS.import} className="flex items-center gap-3 px-4 py-2.5 text-[13px] text-slate-700 hover:bg-slate-50 transition" onClick={() => setActionsOpen(false)}>
                    <Upload size={16} className="text-emerald-600" />
                    Upload Attendance
                  </Link>
                  <Link to={ATTENDANCE_LEGACY_PATHS.import} className="flex items-center gap-3 px-4 py-2.5 text-[13px] text-slate-700 hover:bg-slate-50 transition" onClick={() => setActionsOpen(false)}>
                    <CheckCircle size={16} className="text-amber-600" />
                    Validate &amp; Import
                  </Link>
                  <div className="border-t border-slate-100 my-1" />
                  <Link to="/attendance/workforce" className="flex items-center gap-3 px-4 py-2.5 text-[13px] text-slate-700 hover:bg-slate-50 transition" onClick={() => setActionsOpen(false)}>
                    <RefreshCw size={16} className="text-purple-600" />
                    Sync Biometrics
                  </Link>
                  <Link to="/attendance/punches" className="flex items-center gap-3 px-4 py-2.5 text-[13px] text-slate-700 hover:bg-slate-50 transition" onClick={() => setActionsOpen(false)}>
                    <List size={16} className="text-slate-500" />
                    View Import Log
                  </Link>
                  <div className="border-t border-slate-100 my-1" />
                  <button
                    type="button"
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-[13px] text-slate-700 hover:bg-slate-50 transition text-left"
                    onClick={() => {
                      setActionsOpen(false);
                      const el = document.getElementById("att-audit-section");
                      if (el) el.scrollIntoView({ behavior: "smooth" });
                    }}
                  >
                    <Info size={16} className="text-blue-500" />
                    View Audit Information
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 4) HELP GUIDE — expandable, matching Leave module pattern */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border border-blue-200/60 overflow-hidden mb-5">
        <button
          type="button"
          onClick={() => setShowHelp((v) => !v)}
          className="w-full flex items-center gap-3 px-5 py-3.5 text-left hover:bg-blue-100/40 transition"
        >
          <div className="w-8 h-8 rounded-xl bg-blue-600 text-white flex items-center justify-center shadow-sm shrink-0">
            <HelpCircle size={16} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-bold text-blue-900">How to use Attendance Records</p>
            <p className="text-[11px] text-blue-700/70">Click here for a guide on viewing, filtering, updating attendance, and using Import &amp; Actions</p>
          </div>
          <ChevronDown size={16} className={`text-blue-500 transition-transform ${showHelp ? "rotate-180" : ""}`} />
        </button>
        {showHelp && (
          <div className="px-5 pb-5 border-t border-blue-200/60">
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 mt-4">
              <div className="flex gap-3">
                <span className="shrink-0 w-6 h-6 rounded-lg bg-blue-600 text-white text-[11px] font-bold flex items-center justify-center">1</span>
                <div>
                  <p className="text-[12.5px] font-bold text-slate-800">Filter by Date &amp; Department</p>
                  <p className="text-[11px] text-slate-500">Use the filter bar to select a date, department, specific employee, or attendance status. Click <b className="text-blue-700">Apply Filters</b> to refresh the table.</p>
                </div>
              </div>
              <div className="flex gap-3">
                <span className="shrink-0 w-6 h-6 rounded-lg bg-blue-600 text-white text-[11px] font-bold flex items-center justify-center">2</span>
                <div>
                  <p className="text-[12.5px] font-bold text-slate-800">Status Tabs</p>
                  <p className="text-[11px] text-slate-500">Click <b className="text-emerald-700">Present</b>, <b className="text-amber-700">Leave</b>, or <b className="text-red-600">Absent</b> tabs to quickly filter the table by attendance status without changing other filters.</p>
                </div>
              </div>
              <div className="flex gap-3">
                <span className="shrink-0 w-6 h-6 rounded-lg bg-blue-600 text-white text-[11px] font-bold flex items-center justify-center">3</span>
                <div>
                  <p className="text-[12.5px] font-bold text-slate-800">Bulk Update Status</p>
                  <p className="text-[11px] text-slate-500">Select multiple employees using checkboxes, then click <b className="text-blue-700">Bulk Update Status</b> in the selection bar to change their attendance at once (e.g., mark as Present, Leave, or Absent).</p>
                </div>
              </div>
              <div className="flex gap-3">
                <span className="shrink-0 w-6 h-6 rounded-lg bg-blue-600 text-white text-[11px] font-bold flex items-center justify-center">4</span>
                <div>
                  <p className="text-[12.5px] font-bold text-slate-800">Update Individual Status</p>
                  <p className="text-[11px] text-slate-500">Click on any employee name or their status badge (Present/Leave/Absent) to open a quick editor and change that specific employee's attendance.</p>
                </div>
              </div>
              <div className="flex gap-3">
                <span className="shrink-0 w-6 h-6 rounded-lg bg-blue-600 text-white text-[11px] font-bold flex items-center justify-center">5</span>
                <div>
                  <p className="text-[12.5px] font-bold text-slate-800">Import &amp; Actions</p>
                  <p className="text-[11px] text-slate-500">Use the <b className="text-blue-700">Import &amp; Actions</b> button to: download attendance templates, upload Excel files, validate &amp; import data, sync biometric devices, or view import logs.</p>
                </div>
              </div>
              <div className="flex gap-3">
                <span className="shrink-0 w-6 h-6 rounded-lg bg-blue-600 text-white text-[11px] font-bold flex items-center justify-center">6</span>
                <div>
                  <p className="text-[12.5px] font-bold text-slate-800">Source Badges</p>
                  <p className="text-[11px] text-slate-500">Each record shows how it was created: <b className="text-blue-700">System Default</b> (auto-generated), <b className="text-emerald-700">Manual Entry</b> (HR updated), <b className="text-amber-700">Leave Request</b> (from approved leave), or <b className="text-purple-700">Biometric</b> (device sync).</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ALERTS */}
      {(error || info) && (
        <div
          className="rounded-xl border px-4 py-3 mb-4 text-[13px]"
          style={{
            borderColor: error ? "#fecaca" : "#bbf7d0",
            background: error ? "#fef2f2" : "#f0fdf4",
            color: error ? "#991b1b" : "#166534",
          }}
        >
          {error || info}
        </div>
      )}

      {/* FILTERS */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 mb-4">
        <div className="flex items-end gap-3 flex-wrap">
          <div className="att-mvp-field" style={{ minWidth: 150 }}>
            <label>Date</label>
            <input type="date" className="att-mvp-filter-input" value={filterDate} onChange={(e) => setFilterDate(e.target.value)} />
          </div>
          <div className="att-mvp-field" style={{ minWidth: 160 }}>
            <label>Department</label>
            <select className="att-mvp-filter-select" value={filterDept} onChange={(e) => { setFilterDept(e.target.value); setFilterEmployee(""); }}>
              <option value="">All departments</option>
              {employeeMaps.depts.map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
          </div>
          <div className="att-mvp-field" style={{ minWidth: 180 }}>
            <label>Employee</label>
            <select className="att-mvp-filter-select" value={filterEmployee} onChange={(e) => setFilterEmployee(e.target.value)}>
              <option value="">All employees</option>
              {employees
                .filter((e) => !filterDept || String(e.department?.department_id) === filterDept)
                .map((e) => (
                  <option key={e.employee_id} value={e.employee_id}>
                    {[e.first_name, e.last_name].filter(Boolean).join(" ")} ({e.employee_code})
                  </option>
                ))}
            </select>
          </div>
          <div className="att-mvp-field" style={{ minWidth: 150 }}>
            <label>Attendance Status</label>
            <select className="att-mvp-filter-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value || "all"} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <button type="button" className="att-mvp-filter-btn att-mvp-filter-btn--apply" onClick={applyFilters} disabled={loading}>
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
              Apply Filters
            </button>
            <button type="button" className="att-mvp-filter-reset" onClick={resetFilters}>
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* 2) STATUS TABS — colored */}
      <div className="flex items-center gap-2 mb-3">
        {[
          { key: "", label: "All", count: statusCounts.all, color: "text-blue-700 bg-blue-50 border-blue-200", active: "bg-blue-600 text-white border-blue-600" },
          { key: "P", label: "Present", count: statusCounts.P, color: "text-emerald-700 bg-emerald-50 border-emerald-200", active: "bg-emerald-600 text-white border-emerald-600" },
          { key: "L", label: "Leave", count: statusCounts.L, color: "text-amber-700 bg-amber-50 border-amber-200", active: "bg-amber-500 text-white border-amber-500" },
          { key: "A", label: "Absent", count: statusCounts.A, color: "text-red-700 bg-red-50 border-red-200", active: "bg-red-600 text-white border-red-600" },
        ].map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => { setStatusTab(t.key); setPage(1); }}
            className={`inline-flex items-center gap-1.5 px-4 py-2 text-[13px] font-bold rounded-xl border transition shadow-sm ${
              statusTab === t.key ? t.active : t.color
            }`}
          >
            {t.label}
            <span className={`text-[11px] font-extrabold px-1.5 py-0.5 rounded-md ${
              statusTab === t.key ? "bg-white/25" : "bg-black/5"
            }`}>
              {t.count}
            </span>
          </button>
        ))}
      </div>

      {/* 3) SELECTION BAR — always visible */}
      <div className={`flex items-center justify-between rounded-xl px-5 py-2.5 mb-3 shadow-sm transition ${
        selectedIds.size > 0
          ? "bg-blue-600 text-white"
          : "bg-slate-100 text-slate-500 border border-slate-200"
      }`}>
        <span className="text-[13px] font-bold">
          {selectedIds.size > 0
            ? `${selectedIds.size} Employee${selectedIds.size !== 1 ? "s" : ""} Selected`
            : "No employees selected — use checkboxes to select"}
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-bold rounded-lg transition ${
              selectedIds.size > 0
                ? "bg-white/20 hover:bg-white/30 text-white"
                : "bg-slate-200 text-slate-400 cursor-not-allowed"
            }`}
            onClick={openBulkModal}
            disabled={selectedIds.size === 0}
          >
            <Users size={14} />
            Bulk Update Status
          </button>
          {selectedIds.size > 0 && (
            <button
              type="button"
              className="text-[12px] font-semibold text-white/80 hover:text-white transition"
              onClick={() => setSelectedIds(new Set())}
            >
              Deselect All
            </button>
          )}
        </div>
      </div>

      {/* TABLE — full width */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="att-mvp-table-wrap">
          <table className="att-mvp-table">
            <thead>
              <tr>
                <th style={{ width: 36 }}>
                  <input
                    type="checkbox"
                    checked={pagedRows.length > 0 && pagedRows.every((r) => selectedIds.has(String(r.empId)))}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedIds(new Set(pagedRows.map((r) => String(r.empId))));
                      } else {
                        setSelectedIds(new Set());
                      }
                    }}
                  />
                </th>
                <th>Employee Name</th>
                <th>Date</th>
                <th>In Time</th>
                <th>Out Time</th>
                <th>Attendance Status</th>
                <th>Source</th>
              </tr>
            </thead>
            <tbody>
              {pagedRows.map((row) => {
                const sb = sourceBadge(row.source);
                const orgLevel = isOrgLevelStatus(row.rawStatus || row.status.code);
                return (
                  <tr key={row.id} className={selectedIds.has(String(row.empId)) ? "is-selected" : ""}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(String(row.empId))}
                        onChange={() => toggleRow(row.empId)}
                      />
                    </td>
                    <td>
                      <button
                        type="button"
                        className="att-mvp-emp-status-btn inline-flex items-center gap-2"
                        onClick={() => openRowStatusEdit(row)}
                        title={orgLevel ? `${row.status.label} — organization-level, cannot be changed` : "Change attendance status"}
                      >
                        <span className={`shrink-0 w-7 h-7 rounded-full text-white text-[10px] font-bold flex items-center justify-center ${empAvatarColor(row.name)}`}>
                          {empInitials(row.name)}
                        </span>
                        <span className="text-blue-700 font-semibold hover:underline">{row.name}</span>
                      </button>
                    </td>
                    <td>{formatDate(row.date)}</td>
                    <td>{row.inTime}</td>
                    <td>{row.outTime}</td>
                    <td>
                      <button
                        type="button"
                        className="att-mvp-status-click"
                        onClick={() => openRowStatusEdit(row)}
                        title={orgLevel ? `${row.status.label} — organization-level, cannot be changed` : "Change attendance status"}
                      >
                        <span className={`att-mvp-badge ${row.status.cls}`}>
                          {row.status.label}
                          {row.status.code === "L" && row.leaveTypeCode ? ` — ${row.leaveTypeCode}` : ""}
                        </span>
                        {orgLevel && <Lock size={12} className="ml-1 text-slate-400 inline" />}
                      </button>
                    </td>
                    <td>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[12px] text-slate-600">{row.source}</span>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${sb.cls}`}>{sb.label}</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!pagedRows.length && !loading && (
                <tr>
                  <td colSpan={7} style={{ textAlign: "center", padding: 24, color: "var(--slate-500)" }}>
                    No attendance records for {formatDate(filterDate)}.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="att-mvp-footer att-mvp-records-pagination">
          <span className="att-mvp-records-pagination-summary">
            {displayRows.length
              ? `Showing ${firstIdx} to ${lastIdx} of ${displayRows.length} entries`
              : "No results"}
          </span>
          <div className="att-mvp-records-pagination-controls">
            <select className="att-mvp-records-page-size" value={PAGE_SIZE} disabled aria-label="Results per page">
              <option value={10}>10 per page</option>
            </select>
            <button type="button" className="att-mvp-page-btn" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
              <button key={n} type="button" className={`att-mvp-page-btn${page === n ? " is-active" : ""}`} onClick={() => setPage(n)}>{n}</button>
            ))}
            <button type="button" className="att-mvp-page-btn" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</button>
          </div>
        </div>
      </div>

      {/* AUDIT INFO — after table, aligned right */}
      <div className="flex justify-end mt-4">
        <div id="att-audit-section" className="w-[320px] bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-slate-100">
            <p className="text-[13px] font-bold text-slate-800 flex items-center gap-2">
              <Info size={14} className="text-blue-600" />
              Audit Information
            </p>
          </div>
          <div className="p-4 space-y-3">
            <div>
              <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Last Modified By</p>
              <p className="text-[13px] font-semibold text-slate-700 mt-0.5">{audit?.modifiedBy || "—"}</p>
            </div>
            <div>
              <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Modified Date</p>
              <p className="text-[13px] font-semibold text-slate-700 mt-0.5">{audit?.modifiedDate ? new Date(audit.modifiedDate).toLocaleString() : "—"}</p>
            </div>
            <div>
              <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Attendance Source</p>
              <p className="text-[13px] font-semibold text-slate-700 mt-0.5">{audit?.source || "—"}</p>
            </div>
          </div>
        </div>
      </div>

      {/* BULK UPDATE MODAL */}
      {bulkOpen && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40" style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0 }} onClick={() => !bulkBusy && setBulkOpen(false)}>
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="bulk-status-title">

            {/* Header */}
            <div className="flex items-start justify-between px-8 pt-7 pb-2">
              <h2 id="bulk-status-title" className="text-xl font-extrabold text-slate-900 tracking-tight">Bulk Update Status</h2>
              <button
                type="button"
                onClick={() => setBulkOpen(false)}
                disabled={bulkBusy}
                className="p-1 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition -mt-1 -mr-2 disabled:opacity-50"
              >
                <X size={22} />
              </button>
            </div>

            {/* Description */}
            <p className="px-8 pb-4 text-[15px] text-slate-500">
              Update <strong className="text-slate-800">{selectedIds.size} selected employee(s)</strong> for {formatDate(filterDate)}.
            </p>

            {/* Body */}
            <div className="px-8 pb-5 space-y-5">
              <div>
                <label className="block text-sm font-bold text-slate-800 mb-1.5">
                  New Status <span className="text-red-500">*</span>
                </label>
                <select
                  value={bulkStatus}
                  disabled={bulkBusy}
                  onChange={(e) => { setBulkStatus(e.target.value); setBulkLeaveType(""); setBulkValidation(""); }}
                  className="w-full border border-slate-300 rounded-xl px-4 py-3 text-[15px] text-slate-800 bg-white focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 outline-none disabled:opacity-60 appearance-auto"
                >
                  {EDITABLE_STATUSES.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>

              {bulkStatus === "L" && (
                <div>
                  <label className="block text-sm font-bold text-slate-800 mb-1.5">
                    Leave Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={bulkLeaveType}
                    disabled={bulkBusy}
                    onChange={(e) => { setBulkLeaveType(e.target.value); setBulkValidation(""); }}
                    className="w-full border border-slate-300 rounded-xl px-4 py-3 text-[15px] text-slate-800 bg-white focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 outline-none disabled:opacity-60 appearance-auto"
                  >
                    <option value="">— Select leave type —</option>
                    {leaveTypes.map((lt) => (
                      <option key={lt.code} value={lt.code}>{lt.code} - {lt.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {bulkValidation && (
                <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-2.5">
                  <AlertCircle size={16} className="shrink-0" />
                  {bulkValidation}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-8 py-5 border-t border-slate-100 bg-slate-50/50">
              <button
                type="button"
                onClick={() => setBulkOpen(false)}
                disabled={bulkBusy}
                className="px-6 py-2.5 rounded-xl text-[15px] font-semibold text-slate-700 border border-slate-300 bg-white hover:bg-slate-50 transition disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={applyBulkStatus}
                disabled={bulkBusy || !selectedIds.size}
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl text-[15px] font-bold text-white bg-blue-600 hover:bg-blue-700 shadow-sm transition disabled:opacity-70"
              >
                {bulkBusy ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Updating…
                  </>
                ) : (
                  <>
                    <Check size={16} />
                    Save
                  </>
                )}
              </button>
            </div>

            {bulkStatus === "L" && (
              <div className="flex items-center gap-2.5 px-8 py-3 bg-blue-50 border-t border-blue-100 text-[13px] text-blue-700">
                <Info size={16} className="shrink-0 text-blue-500" />
                Leave type is required when status is Leave.
              </div>
            )}

          </div>
        </div>,
        document.body
      )}

      {/* ROW EDIT MODAL */}
      {rowEdit && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40" style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0 }} onClick={() => !rowEditBusy && !rowEditSuccess && setRowEdit(null)}>
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="row-status-title">

            {rowEditSuccess ? (
              <div className="px-8 py-10 flex flex-col items-center justify-center gap-3">
                <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
                  <CheckCircle2 className="w-9 h-9 text-emerald-600" />
                </div>
                <p className="text-xl font-bold text-emerald-700">Saved successfully</p>
              </div>
            ) : (
              <>
                {/* Header */}
                <div className="flex items-start justify-between px-8 pt-7 pb-2">
                  <h2 id="row-status-title" className="text-xl font-extrabold text-slate-900 tracking-tight">Update Employee Status</h2>
                  <button
                    type="button"
                    onClick={() => setRowEdit(null)}
                    disabled={rowEditBusy}
                    className="p-1 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition -mt-1 -mr-2 disabled:opacity-50"
                  >
                    <X size={22} />
                  </button>
                </div>

                {/* Description */}
                <p className="px-8 pb-4 text-[15px] text-slate-500">
                  Change status for <strong className="text-slate-800">{rowEdit.name}</strong> on {formatDate(rowEdit.date || filterDate)}.
                </p>

                {/* Body */}
                <div className="px-8 pb-5 space-y-5">
                  {/* New Status */}
                  <div>
                    <label className="block text-sm font-bold text-slate-800 mb-1.5">
                      New Status <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={rowEditStatus}
                      disabled={rowEditBusy}
                      onChange={(e) => { setRowEditStatus(e.target.value); setRowEditLeaveType(""); setRowEditValidation(""); }}
                      className="w-full border border-slate-300 rounded-xl px-4 py-3 text-[15px] text-slate-800 bg-white focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 outline-none disabled:opacity-60 appearance-auto"
                    >
                      {EDITABLE_STATUSES.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Leave Type */}
                  {rowEditStatus === "L" && (
                    <div>
                      <label className="block text-sm font-bold text-slate-800 mb-1.5">
                        Leave Type <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={rowEditLeaveType}
                        disabled={rowEditBusy}
                        onChange={(e) => { setRowEditLeaveType(e.target.value); setRowEditValidation(""); }}
                        className="w-full border border-slate-300 rounded-xl px-4 py-3 text-[15px] text-slate-800 bg-white focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 outline-none disabled:opacity-60 appearance-auto"
                      >
                        <option value="">— Select leave type —</option>
                        {leaveTypes.map((lt) => (
                          <option key={lt.code} value={lt.code}>{lt.code} - {lt.name}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Validation error */}
                  {rowEditValidation && (
                    <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-2.5">
                      <AlertCircle size={16} className="shrink-0" />
                      {rowEditValidation}
                    </div>
                  )}
                </div>

                {/* Footer buttons */}
                <div className="flex items-center justify-end gap-3 px-8 py-5 border-t border-slate-100 bg-slate-50/50">
                  <button
                    type="button"
                    onClick={() => setRowEdit(null)}
                    disabled={rowEditBusy}
                    className="px-6 py-2.5 rounded-xl text-[15px] font-semibold text-slate-700 border border-slate-300 bg-white hover:bg-slate-50 transition disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={applyRowStatus}
                    disabled={rowEditBusy}
                    className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl text-[15px] font-bold text-white bg-blue-600 hover:bg-blue-700 shadow-sm transition disabled:opacity-70"
                  >
                    {rowEditBusy ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        Saving…
                      </>
                    ) : (
                      <>
                        <Check size={16} />
                        Save
                      </>
                    )}
                  </button>
                </div>

                {/* Hint bar */}
                {rowEditStatus === "L" && (
                  <div className="flex items-center gap-2.5 px-8 py-3 bg-blue-50 border-t border-blue-100 text-[13px] text-blue-700">
                    <Info size={16} className="shrink-0 text-blue-500" />
                    Leave type is required when status is Leave.
                  </div>
                )}
              </>
            )}

          </div>
        </div>,
        document.body
      )}

      {/* INFO MODAL for Holiday / Week Off */}
      {infoModal && createPortal(
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/30"
          style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0 }}
          onClick={() => setInfoModal(null)}
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xs mx-4 overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className={`px-5 py-4 ${infoModal.type === "H" ? "bg-violet-50" : "bg-slate-50"}`}>
              <h3 className={`text-base font-bold ${infoModal.type === "H" ? "text-violet-900" : "text-slate-900"}`}>
                {infoModal.type === "H" ? "Holiday" : "Week Off"}
              </h3>
              {infoModal.type === "H" && infoModal.holidayName && (
                <p className="text-sm font-semibold text-violet-700 mt-1">{infoModal.holidayName}</p>
              )}
            </div>
            <div className="px-5 py-4">
              <p className="text-sm text-slate-600 leading-relaxed">
                {infoModal.type === "H"
                  ? "This holiday is configured at the organization level and cannot be changed from employee attendance."
                  : "This day is configured as an organization-level weekly off and cannot be changed from employee attendance."}
              </p>
            </div>
            <div className="flex justify-end px-5 py-3 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setInfoModal(null)}
                className="px-4 py-2 rounded-xl text-sm font-semibold text-slate-700 hover:bg-slate-100 transition"
              >
                OK
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

function parseLeaveTypeFromRemarks(remarks) {
  if (!remarks) return "";
  const m = String(remarks).match(/leave:(\w+)/);
  return m ? m[1] : "";
}
